import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input, { Select, Textarea } from '../../components/ui/Input';
import toast from 'react-hot-toast';

export default function IssueCertificate() {
  const [orgs, setOrgs] = useState([]);
  const [appliances, setAppliances] = useState([]);
  const [form, setForm] = useState({
    organizationId: '',
    softwareName: '',
    licenseType: 'perpetual',
    userLicenseCount: 1,
    applianceId: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    async function load() {
      try {
        const { data } = await api.get('/admin/organizations');
        setOrgs(data.organizations);
      } catch (err) {
        console.error('Load orgs error:', err);
      }
    }
    load();
  }, []);

  useEffect(() => {
    if (!form.organizationId) { setAppliances([]); return; }
    async function loadAppliances() {
      try {
        const { data } = await api.get(`/appliances?limit=100`);
        setAppliances(data.appliances.filter(a => a.organizationId === parseInt(form.organizationId)));
      } catch (err) {
        console.error('Load appliances error:', err);
      }
    }
    loadAppliances();
  }, [form.organizationId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        organizationId: parseInt(form.organizationId),
        userLicenseCount: parseInt(form.userLicenseCount),
        applianceId: form.applianceId ? parseInt(form.applianceId) : undefined,
      };
      const { data } = await api.post('/certificates', payload);
      setResult(data.certificate);
      toast.success('Certificate issued successfully!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to issue certificate');
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div className="max-w-2xl">
        <Card className="text-center py-10">
          <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-slate-100 mb-2">Certificate Issued</h3>
          <div className="bg-navy-900 rounded-lg p-4 mt-4 inline-block">
            <p className="text-xs text-slate-400 uppercase tracking-wider">License Key</p>
            <p className="text-2xl font-mono font-bold text-cyan-400 mt-1 tracking-widest">{result.licenseKey}</p>
          </div>
          <p className="text-sm text-slate-400 mt-4">{result.softwareName}</p>
          <p className="text-sm text-slate-400">{result.organization?.name}</p>

          <div className="flex justify-center gap-3 mt-8">
            <Button variant="secondary" onClick={() => navigate('/admin/certificates')}>View All Certificates</Button>
            <Button onClick={() => { setResult(null); setForm({ organizationId: '', softwareName: '', licenseType: 'perpetual', userLicenseCount: 1, applianceId: '', notes: '' }); }}>
              Issue Another
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-100 mb-2">Issue Certificate</h1>
      <p className="text-slate-400 mb-8">Generate a new license certificate with PDFs for a client</p>

      <Card className="max-w-2xl">
        <form onSubmit={handleSubmit} className="space-y-5">
          <Select
            label="Organization"
            value={form.organizationId}
            onChange={(e) => setForm({ ...form, organizationId: e.target.value })}
            required
          >
            <option value="">Select organization...</option>
            {orgs.map(o => (
              <option key={o.id} value={o.id}>{o.name} {o.shortName ? `(${o.shortName})` : ''}</option>
            ))}
          </Select>

          <Input
            label="Software Name"
            value={form.softwareName}
            onChange={(e) => setForm({ ...form, softwareName: e.target.value })}
            placeholder="e.g., Security Cyber Platform License"
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="License Type"
              value={form.licenseType}
              onChange={(e) => setForm({ ...form, licenseType: e.target.value })}
            >
              <option value="perpetual">Perpetual</option>
              <option value="annual">Annual</option>
              <option value="trial">Trial</option>
            </Select>

            <Input
              label="User License Count"
              type="number"
              min={1}
              value={form.userLicenseCount}
              onChange={(e) => setForm({ ...form, userLicenseCount: e.target.value })}
            />
          </div>

          {appliances.length > 0 && (
            <Select
              label="Link to Appliance (Optional)"
              value={form.applianceId}
              onChange={(e) => setForm({ ...form, applianceId: e.target.value })}
            >
              <option value="">No appliance linked</option>
              {appliances.map(a => (
                <option key={a.id} value={a.id}>{a.unitName} ({a.serialNumber})</option>
              ))}
            </Select>
          )}

          <Textarea
            label="Notes (Optional)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Internal notes about this certificate..."
          />

          <div className="pt-2">
            <Button type="submit" disabled={loading} size="lg">
              {loading ? 'Generating Certificate...' : 'Issue Certificate'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
